import { useEffect, useState, useCallback } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import { useNavigate } from "react-router-dom";
import { rpcCall, subscribe } from "../ws/client";
import { useNetwork } from "../hooks/useNetwork";
import { getJettonsForNetwork } from "../config/jettons";
import "../styles/WalletBalance.scss";

interface BalanceInfo {
    ton_balance: number;
    balances: { [symbol: string]: number };
}

interface CachedBalanceInfo extends BalanceInfo {
    timestamp: number;
    wallet: string;
}

interface ExchangeRate {
    ton_rub: number | null;
    ton_usd: number | null;
    usd_rub: number | null;
}

interface JettonRates {
    [jettonAddress: string]: number; // RUB per jetton
}

// Format large numbers with spaces (e.g., 1234567 -> 1 234 567)
const formatNumber = (num: number, decimals: number = 0): string => {
    const fixed = num.toFixed(decimals);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.');
};

export default function WalletBalance() {
    const wallet = useTonWallet();
    const navigate = useNavigate();
    const { network, loading: networkLoading } = useNetwork();
    const [balances, setBalances] = useState<BalanceInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [isFromCache, setIsFromCache] = useState(false);
    const [exchangeRate, setExchangeRate] = useState<ExchangeRate>({ ton_rub: null, ton_usd: null, usd_rub: null });
    const [jettonRates, setJettonRates] = useState<JettonRates>({});

    // Cache key for localStorage
    const getCacheKey = useCallback((walletAddress: string) => {
        return `wallet_balance_${walletAddress}_${network}`;
    }, [network]);

    // Load balance from cache
    const loadFromCache = useCallback((walletAddress: string): BalanceInfo | null => {
        try {
            const cacheKey = getCacheKey(walletAddress);
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const cachedData: CachedBalanceInfo = JSON.parse(cached);
                // Use cache if less than 5 minutes old
                const age = Date.now() - cachedData.timestamp;
                if (age < 5 * 60 * 1000) { // 5 minutes
                    console.log(`[WalletBalance] Loaded from cache (age: ${Math.round(age / 1000)}s)`);
                    return {
                        ton_balance: cachedData.ton_balance,
                        balances: cachedData.balances
                    };
                }
            }
        } catch (err) {
            console.error("[WalletBalance] Failed to load from cache:", err);
        }
        return null;
    }, [getCacheKey]);

    // Save balance to cache
    const saveToCache = useCallback((walletAddress: string, balanceData: BalanceInfo) => {
        try {
            const cacheKey = getCacheKey(walletAddress);
            const cacheData: CachedBalanceInfo = {
                ...balanceData,
                timestamp: Date.now(),
                wallet: walletAddress
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log("[WalletBalance] Saved to cache");
        } catch (err) {
            console.error("[WalletBalance] Failed to save to cache:", err);
        }
    }, [getCacheKey]);

    // Load exchange rate
    const loadExchangeRate = useCallback(async () => {
        try {
            const response = await rpcCall<{}, ExchangeRate>("exchange_rate.get", {});
            if (response.result) {
                setExchangeRate(response.result);
            }
        } catch (err) {
            console.error("Failed to load exchange rate:", err);
        }
    }, []);

    // Load jetton rates
    const loadJettonRates = useCallback(async () => {
        const jettons = getJettonsForNetwork(network);
        const jettonAddresses = jettons.map(j => j.address);
        
        try {
            const response = await rpcCall<{ jetton_addresses: string[] }, { jetton_rates: JettonRates }>(
                "exchange_rate.jettons",
                { jetton_addresses: jettonAddresses }
            );
            if (response.result?.jetton_rates) {
                setJettonRates(response.result.jetton_rates);
            }
        } catch (err) {
            console.error("Failed to load jetton rates:", err);
        }
    }, [network]);

    /**
     * Load wallet balances with smart caching strategy:
     * 1. Request all jetton balances in parallel
     * 2. Detect rate limit errors (429)
     * 3. If rate limit detected → try to load from cache
     * 4. If no rate limit → save successful results to cache
     * 5. Don't cache all-zero balances (likely API error)
     */
    const loadBalances = useCallback(async () => {
        if (!wallet) return;

        // Get all jettons for current network
        const jettons = getJettonsForNetwork(network);
        
        console.log('[WalletBalance] Loading balances for jettons:', jettons.map(j => j.symbol));
        
        setLoading(true);
        setIsFromCache(false);
        
        try {
            let tonBalance = 0;
            const jettonBalances: { [symbol: string]: number } = {};
            let hasRateLimitError = false;

            // Load all balances in PARALLEL for better performance
            const balancePromises = jettons.map(async (jetton) => {
                try {
                    console.log(`[WalletBalance] Requesting ${jetton.symbol} balance...`);
                    
                    const response = await rpcCall<any, { 
                        can_sell: boolean;
                        ton_balance: number;
                        jetton_balance: number;
                        min_ton_required: number;
                        jetton_required: number;
                    }>(
                        "wallet.check_balance",
                        {
                            wallet: wallet.account.address,
                            jetton_address: jetton.address,
                            amount: 0.01 // Small amount just to check balance
                        }
                    );
                    
                    if (response.result) {
                        const balance = response.result.jetton_balance || 0;
                        console.log(`[WalletBalance] ✅ ${jetton.symbol}: ${balance}`);
                        return { symbol: jetton.symbol, balance, ton_balance: response.result.ton_balance, error: null };
                    } else if (response.error) {
                        const errorMsg = response.error.message || '';
                        // Check for rate limit errors
                        if (errorMsg.includes('rate limit') || errorMsg.includes('429') || errorMsg.includes('timeout')) {
                            hasRateLimitError = true;
                            console.warn(`[WalletBalance] ⚠️ ${jetton.symbol} rate limit error`);
                        } else {
                            console.error(`[WalletBalance] ❌ ${jetton.symbol} error:`, response.error);
                        }
                        return { symbol: jetton.symbol, balance: 0, ton_balance: 0, error: errorMsg };
                    }
                } catch (err) {
                    console.error(`[WalletBalance] ❌ Failed to load ${jetton.symbol}:`, err);
                    return { symbol: jetton.symbol, balance: 0, ton_balance: 0, error: String(err) };
                }
            });

            // Wait for all balance requests to complete
            const results = await Promise.all(balancePromises);
            
            // Process results
            results.forEach(result => {
                if (result) {
                    jettonBalances[result.symbol] = result.balance;
                    // Use max TON balance from all responses
                    if (result.ton_balance > tonBalance) {
                        tonBalance = result.ton_balance;
                    }
                }
            });

            // If rate limit error occurred, try to load from cache
            if (hasRateLimitError) {
                const cached = loadFromCache(wallet.account.address);
                if (cached) {
                    console.log('[WalletBalance] Using cached balances due to rate limit error');
                    setBalances(cached);
                    setIsFromCache(true);
                    setLoading(false);
                    return;
                } else {
                    console.warn('[WalletBalance] ⚠️ Rate limit error and no cache available, showing incomplete data');
                }
            }

            const balanceData = {
                ton_balance: tonBalance,
                balances: jettonBalances
            };

            console.log('[WalletBalance] Final balances:', balanceData);
            setBalances(balanceData);
            
            // Save to cache ONLY if no rate limit errors occurred
            // This prevents caching incomplete/zero balances from failed API calls
            if (!hasRateLimitError) {
                // Additional check: don't cache if all balances are suspiciously zero
                const hasNonZeroBalance = tonBalance > 0 || Object.values(jettonBalances).some(b => b > 0);
                if (hasNonZeroBalance) {
                    saveToCache(wallet.account.address, balanceData);
                } else {
                    console.warn('[WalletBalance] ⚠️ All balances are zero, not caching (might be API error)');
                }
            } else {
                console.log('[WalletBalance] Not caching due to rate limit errors');
            }
            
        } catch (err) {
            console.error("[WalletBalance] Failed to load balances:", err);
            
            // Try to load from cache on error
            const cached = loadFromCache(wallet.account.address);
            if (cached) {
                console.log('[WalletBalance] Using cached balances due to error');
                setBalances(cached);
                setIsFromCache(true);
            }
        } finally {
            setLoading(false);
        }
    }, [wallet, network, loadFromCache, saveToCache]);

    useEffect(() => {
        // Wait for network to be loaded before checking balance
        if (!wallet) {
            setBalances(null);
            return;
        }
        
        if (networkLoading) {
            // Still loading network, don't check balance yet
            return;
        }
        
        // Initial load - fetch everything
        loadBalances();
        loadExchangeRate();
        loadJettonRates();
        
        // Subscribe to balance updates
        const unsubscribeBalance = subscribe("balance.updated", () => {
            loadBalances();
        });
        
        // Subscribe to rate updates
        const unsubscribeRates = subscribe("rates.updated", () => {
            loadExchangeRate();
            loadJettonRates();
        });
        
        return () => {
            unsubscribeBalance();
            unsubscribeRates();
        };
    }, [wallet, networkLoading, network, loadBalances, loadExchangeRate, loadJettonRates]);

    if (!wallet) {
        return null;
    }

    const jettons = getJettonsForNetwork(network);
    
    // Handle click on balance card to navigate to liquidity pool or orders
    const handleCardClick = (jettonAddress: string, jettonSymbol: string) => {
        // TON doesn't have liquidity pool - open orders page with filters
        if (jettonSymbol === "TON") {
            navigate(`/?tab=orders&side=buy&jetton=TON`);
        } else {
            // Other jettons - open liquidity pool
            navigate(`/?tab=liquidity&jetton=${encodeURIComponent(jettonAddress)}`);
        }
    };
    
    // Calculate total in RUB
    let totalRub = balances && exchangeRate.ton_rub 
        ? balances.ton_balance * exchangeRate.ton_rub
        : 0;
    
    // Add jettons value in RUB
    if (balances && exchangeRate.usd_rub) {
        jettons.filter(j => j.symbol !== "TON").forEach(jetton => {
            const jettonBalance = balances.balances[jetton.symbol] || 0;
            if (jettonBalance > 0) {
                // For USDT, use USD/RUB rate
                if (jetton.symbol === "USDT") {
                    totalRub += jettonBalance * exchangeRate.usd_rub!;
                } else {
                    // For TMC, ODDB - use escrow rate
                    const rate = jettonRates[jetton.address];
                    if (rate) {
                        totalRub += jettonBalance * rate;
                    }
                }
            }
        });
    }

    return (
        <div className="wallet-balance-container">
            {isFromCache && (
                <div className="cache-notice">
                    ⚠️ Показаны последние известные балансы (API временно недоступен)
                </div>
            )}
            <div className="balance-grid">
                {/* TON Balance */}
                <div 
                    className="balance-card ton clickable"
                    onClick={() => handleCardClick("TON", "TON")}
                    title="Перейти к ордерам TON"
                >
                    <div className="balance-icon">
                        <img 
                            src="https://ton.org/download/ton_symbol.png" 
                            alt="TON"
                            onError={(e) => e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="70" font-size="70">💎</text></svg>'}
                        />
                    </div>
                    <div className="balance-info">
                        <div className="balance-label">TON</div>
                        <div className="balance-amount">
                            {loading ? (
                                <span className="skeleton">...</span>
                            ) : (
                                <>
                                    <span className="main-value">{balances ? balances.ton_balance.toFixed(4) : "0.0000"}</span>
                                    {balances && exchangeRate.ton_rub && (
                                        <span className="rub-value">{formatNumber(balances.ton_balance * exchangeRate.ton_rub)} ₽</span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Jetton Balances */}
                {jettons.filter(j => j.symbol !== "TON").map((jetton, idx) => {
                    const jettonBalance = balances?.balances[jetton.symbol] || 0;
                    let rubValue = 0;
                    
                    if (jettonBalance > 0) {
                        if (jetton.symbol === "USDT" && exchangeRate.usd_rub) {
                            rubValue = jettonBalance * exchangeRate.usd_rub;
                        } else {
                            const rate = jettonRates[jetton.address];
                            if (rate) {
                                rubValue = jettonBalance * rate;
                            }
                        }
                    }
                    
                    return (
                        <div 
                            key={jetton.symbol} 
                            className={`balance-card jetton jetton-${idx} clickable`}
                            onClick={() => handleCardClick(jetton.address, jetton.symbol)}
                            title="Перейти к пулу ликвидности"
                        >
                            <div className="balance-icon">
                                <img 
                                    src={jetton.icon} 
                                    alt={jetton.symbol}
                                    onError={(e) => e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="70" font-size="70">🪙</text></svg>'}
                                />
                            </div>
                            <div className="balance-info">
                                <div className="balance-label">{jetton.symbol}</div>
                                <div className="balance-amount">
                                    {loading ? (
                                        <span className="skeleton">...</span>
                                    ) : (
                                        <>
                                            <span className="main-value">{formatNumber(jettonBalance, 2)}</span>
                                            {rubValue > 0 && (
                                                <span className="rub-value">{formatNumber(rubValue)} ₽</span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
